import { getTimeTrackingForUserSummedPerAnnotation } from "admin/rest_api";
import { Col, Divider, Row } from "antd";
import dayjs from "dayjs";
import { formatMilliseconds } from "libs/format_utils";
import { useQueryWithErrorHandling } from "libs/react_hooks";
import groupBy from "lodash-es/groupBy";
import type { ReactElement } from "react";
import { Fragment } from "react";
import type { APITimeTrackingPerAnnotation } from "types/api_types";
import type { AnnotationStateFilterEnum, AnnotationTypeFilterEnum } from "viewer/constants";
import { AnnotationStats } from "viewer/view/right_border_tabs/info_tab/annotation_stats_section";

type TimeTrackingDetailViewProps = {
  userId: string;
  dateRange: [number, number];
  annotationType: AnnotationTypeFilterEnum;
  annotationState: AnnotationStateFilterEnum;
  projectIds: string[];
};

const ANNOTATION_OR_TASK_NAME_SPAN = 16;
const STATISTICS_SPAN = 4;
const TIMESPAN_SPAN = 4;

const STYLING_CLASS_NAME = "time-tracking-details";

const renderRow = (
  userDataPerAnnotation: APITimeTrackingPerAnnotation[],
): [ReactElement[], ReactElement[]] => {
  if (userDataPerAnnotation == null) return [[], []];
  const groupedByProject = groupBy(userDataPerAnnotation, "projectName");
  let taskRows: ReactElement[] = [];
  let annotationRows: ReactElement[] = [];

  for (const [project, loggedTimes] of Object.entries(groupedByProject)) {
    if (project === "null") {
      // annotations (as opposed to tasks)
      const tableRows = loggedTimes.map((timeEntry, i) => (
        <Row key={`time_row_${i}`}>
          <Col span={ANNOTATION_OR_TASK_NAME_SPAN}>
            <a href={`annotations/${timeEntry.annotation}`}>Annotation: {timeEntry.annotation} </a>
          </Col>
          <Col span={STATISTICS_SPAN}>
            <AnnotationStats stats={timeEntry.annotationLayerStats} />
          </Col>
          <Col span={TIMESPAN_SPAN}>{formatMilliseconds(timeEntry.timeMillis)}</Col>
        </Row>
      ));
      annotationRows = annotationRows.concat(tableRows);
    } else {
      // tasks
      taskRows.push(
        <Row key={project} style={{ fontWeight: "bold", margin: "5px 20px" }}>
          <Col>{project}</Col>
        </Row>,
      );
      const tableRows = loggedTimes.map((timeEntry, i) => (
        <Row key={`time_row_${i}`}>
          <Col span={ANNOTATION_OR_TASK_NAME_SPAN}>
            <a href={`annotations/${timeEntry.annotation}`}>Task: {timeEntry.task}</a>
          </Col>
          <Col span={STATISTICS_SPAN}>
            <AnnotationStats stats={timeEntry.annotationLayerStats} />
          </Col>
          <Col span={TIMESPAN_SPAN}>{formatMilliseconds(timeEntry.timeMillis)}</Col>
        </Row>
      ));
      taskRows = taskRows.concat(tableRows);
    }
  }
  return [annotationRows, taskRows];
};

function TimeTrackingDetailView({
  userId,
  dateRange,
  annotationType,
  annotationState,
  projectIds,
}: TimeTrackingDetailViewProps) {
  const { data: userData = [] } = useQueryWithErrorHandling({
    queryKey: [
      "timeTrackingPerAnnotation",
      userId,
      dateRange,
      annotationType,
      annotationState,
      projectIds,
    ],
    queryFn: () =>
      getTimeTrackingForUserSummedPerAnnotation(
        userId,
        dayjs(dateRange[0]),
        dayjs(dateRange[1]),
        annotationType,
        annotationState,
        projectIds,
      ),
  });

  const [annotationRows, taskRows] = renderRow(userData);
  const rowsNoDivider = annotationRows.concat(taskRows);
  const rowLength = rowsNoDivider.length;
  const rows = rowsNoDivider.map((row, index) => {
    if (index < rowLength - 1)
      return (
        <Fragment key={index}>
          {row} <Divider style={{ margin: 0 }} />
        </Fragment>
      );
    return row;
  });
  return <div className={STYLING_CLASS_NAME}>{rows}</div>;
}

export default TimeTrackingDetailView;
